import { LoaderCircle } from "lucide-react";

import { ErrorText, PageHeader } from "../components/page-header";
import { useActive } from "../lib/active";
import { apiErrorMessage } from "../lib/api-error";
import { formatRelative } from "../lib/format";
import { useAuditLog } from "../lib/workspace";

const describe = (data: Record<string, unknown>) =>
  Object.entries(data)
    .filter(([, value]) => value !== null && value !== undefined && typeof value !== "object")
    .map(([key, value]) => `${key}: ${String(value)}`)
    .join(" · ");

/** Who changed what in this workspace: people and, later, agents. Owners only. */
export const ActivityPage = () => {
  const { workspace, atLeast } = useActive();
  const log = useAuditLog();

  if (!atLeast("OWNER")) {
    return <p className="text-sm text-muted">Only owners can see the activity log.</p>;
  }

  return (
    <section className="max-w-4xl space-y-6">
      <PageHeader eyebrow={workspace?.name ?? ""} title="Activity">
        The last 100 changes to members, brands, knowledge, claims and settings.
      </PageHeader>
      {log.isError && <ErrorText>{apiErrorMessage(log.error)}</ErrorText>}
      {log.isPending && (
        <LoaderCircle className="size-5 animate-spin text-muted" aria-label="Loading" />
      )}
      <ol className="divide-y divide-line rounded-xl border border-line bg-white">
        {log.data?.length === 0 && <li className="px-4 py-6 text-sm text-muted">Nothing yet.</li>}
        {log.data?.map((event) => (
          <li
            key={event.id}
            className="grid gap-1 px-4 py-3 text-sm sm:grid-cols-[10rem_minmax(0,1fr)]"
          >
            <span className="text-muted">{formatRelative(event.createdAt)}</span>
            <span className="min-w-0">
              <strong className="font-medium">{event.actor}</strong> · <code>{event.action}</code>
              {describe(event.data) && (
                <span className="block truncate text-xs text-muted">{describe(event.data)}</span>
              )}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
};
